import { useState, useEffect, useCallback } from 'react';

/**
 * Simplified PWA Manager
 * Handles PWA installation detection and setup flow
 */
class SimplePWAManager {
  constructor() {
    this.deferredPrompt = null;
    this.listeners = new Set();
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      this.deferredPrompt = event;
      this.notifyListeners('promptAvailable');
    });

    // Listen for app installation
    window.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
      // Mark as installed in localStorage - this is the ONLY way we set this flag
      localStorage.setItem('pwa-installed', 'true');
      localStorage.setItem('pwa-install-date', new Date().toISOString());
      this.notifyListeners('installed');
    });
  }

  /**
   * Get current PWA state
   * @param {boolean} isAuthenticated - Whether user is logged in
   */
  getState(isAuthenticated = false) {
    // Check if currently running in standalone mode (PWA is open)
    const isInStandaloneMode =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    // Check if PWA was installed (only trust localStorage set by 'appinstalled' event)
    const isPWAInstalled = localStorage.getItem('pwa-installed') === 'true';

    // there is a bug where the pwa is installed but this is not set in the local storage
    // so we check if the app was openend in standalone mode
    // if this is the case, then we still set the pwa installed flag
    if (isInStandaloneMode) {
      localStorage.setItem('pwa-installed', 'true');
    }

    // Check if we have a deferred install prompt available
    const canInstallPWA = this.deferredPrompt !== null;

    // Check notification permission
    const notificationPermission = 'Notification' in window ? Notification.permission : 'default';
    const hasNotificationPermission = notificationPermission === 'granted';

    // Check if setup is complete (PWA installed AND notifications enabled)
    const setupComplete = isPWAInstalled && hasNotificationPermission;

    // Determine what to show - only show banners when user is authenticated
    const shouldShowSetupBanner =
      isAuthenticated && !setupComplete && (canInstallPWA || this.canInstallManually());
    const shouldShowOpenInAppBanner = isAuthenticated && isPWAInstalled && !isInStandaloneMode;

    return {
      // Core states
      isInStandaloneMode,
      isPWAInstalled,
      canInstallPWA,
      hasNotificationPermission,
      setupComplete,

      // UI states
      shouldShowSetupBanner,
      shouldShowOpenInAppBanner,

      // Additional info
      notificationPermission,
      platform: this.getPlatform(),
      browser: this.getBrowser(),
      supportsNotifications: 'Notification' in window,
      supportsPWA: this.supportsPWA(),
    };
  }

  /**
   * Install PWA and request notifications
   */
  async installPWA() {
    try {
      if (this.deferredPrompt) {
        // Use native install prompt
        const choiceResult = await this.deferredPrompt.prompt();
        this.deferredPrompt = null;

        if (choiceResult.outcome === 'accepted') {
          // Request notifications after installation
          await this.requestNotifications();
          return { success: true, method: 'native' };
        } else {
          return { success: false, reason: 'User declined installation' };
        }
      } else {
        // Manual installation required
        return {
          success: false,
          reason: 'Manual installation required',
          needsManualInstall: true,
          instructions: this.getInstallInstructions(),
        };
      }
    } catch (error) {
      console.error('PWA installation failed:', error);
      return { success: false, reason: error.message };
    }
  }

  /**
   * Request notification permissions
   */
  async requestNotifications() {
    if (!('Notification' in window)) {
      return { success: false, reason: 'Notifications not supported' };
    }

    if (Notification.permission === 'granted') {
      return { success: true, reason: 'Already granted' };
    }

    try {
      const permission = await Notification.requestPermission();
      const success = permission === 'granted';

      this.notifyListeners('notificationPermission', { permission, success });

      return {
        success,
        permission,
        reason: success ? 'Permission granted' : 'Permission denied',
      };
    } catch (error) {
      console.error('Notification permission request failed:', error);
      return { success: false, reason: error.message };
    }
  }

  /**
   * Open the installed PWA with proper platform handling
   */
  openInApp() {
    const state = this.getState();

    // Don't try to open if PWA isn't installed
    if (!state.isPWAInstalled) {
      console.warn('PWA not installed, cannot open in app');
      this.notifyListeners('openInAppFailed', { reason: 'not_installed' });
      return;
    }

    // Already in standalone mode, no need to redirect
    if (state.isInStandaloneMode) {
      console.log('Already running in PWA mode');
      return;
    }

    const platform = this.getPlatform();
    const currentUrl = window.location.href;

    try {
      switch (platform) {
        case 'ios':
          this._openInApp_iOS(currentUrl);
          break;
        case 'android':
          this._openInApp_Android(currentUrl);
          break;
        default:
          this._openInApp_Desktop(currentUrl);
          break;
      }

      this.notifyListeners('openInAppAttempted', { platform, url: currentUrl });
    } catch (error) {
      console.error('Failed to open PWA:', error);
      this.notifyListeners('openInAppFailed', { error: error.message });
      // Fallback to simple redirect
      window.location.href = window.location.origin;
    }
  }

  /**
   * iOS-specific app opening strategy
   * @private
   */
  _openInApp_iOS(url) {
    // For iOS, we need to be more aggressive to trigger PWA opening

    // Method 1: Try opening in new window first (Safari's PWA detection)
    const appWindow = window.open(url, '_blank', 'noopener');

    // Method 2: Fallback with delay to current window redirect
    setTimeout(() => {
      if (appWindow && !appWindow.closed) {
        appWindow.close();
      }
      // Redirect current window - iOS Safari should detect PWA
      window.location.href = url;
    }, 1000);
  }

  /**
   * Android-specific app opening strategy
   * @private
   */
  _openInApp_Android(url) {
    // Android WebAPK/TWA opening strategies

    // For now, use enhanced redirect with parameters
    const appUrl = new URL(url);
    appUrl.searchParams.set('source', 'openInApp');
    appUrl.searchParams.set('timestamp', Date.now().toString());

    // Try the enhanced URL
    window.location.href = appUrl.toString();
  }

  /**
   * Desktop-specific app opening strategy
   * @private
   */
  _openInApp_Desktop(url) {
    // Desktop PWAs usually open in their own window context
    // Add parameters to help identify this as an "open in app" request
    const appUrl = new URL(url);
    appUrl.searchParams.set('openInApp', 'true');
    appUrl.searchParams.set('source', 'webBrowser');

    // On desktop, simple redirect usually works
    window.location.href = appUrl.toString();
  }

  /**
   * Enhanced method that includes user feedback
   */
  openInAppWithFeedback() {
    const state = this.getState();

    if (!state.isPWAInstalled) {
      // Show user that PWA isn't installed
      this.notifyListeners('showInstallPrompt', {
        message: 'App not installed. Would you like to install it now?',
      });
      return;
    }

    if (state.isInStandaloneMode) {
      // Already in app, maybe show a message
      this.notifyListeners('alreadyInApp', {
        message: 'You are already using the app!',
      });
      return;
    }

    // Show loading/transition feedback
    this.notifyListeners('openingInApp', {
      message: 'Opening in app...',
    });

    // Small delay for user feedback, then open
    setTimeout(() => {
      this.openInApp();
    }, 500);
  }

  /**
   * Check if we should show "Open in App" button
   * This method fits well with your existing getState() pattern
   */
  shouldShowOpenInAppButton() {
    const state = this.getState(true); // Assuming user is authenticated if asking
    return state.shouldShowOpenInAppBanner;
  }

  /**
   * Get a complete app opening status for UI components
   */
  getOpenInAppStatus() {
    const state = this.getState();

    return {
      canOpenInApp: state.isPWAInstalled && !state.isInStandaloneMode,
      isInStandaloneMode: state.isInStandaloneMode,
      isPWAInstalled: state.isPWAInstalled,
      platform: state.platform,
      shouldShowButton: this.shouldShowOpenInAppButton(),
      buttonText: this._getOpenInAppButtonText(state),
    };
  }

  /**
   * Get appropriate button text based on platform
   * @private
   */
  _getOpenInAppButtonText(state) {
    if (!state.isPWAInstalled) return 'Install App';
    if (state.isInStandaloneMode) return 'Already in App';

    switch (state.platform) {
      case 'ios':
        return 'Open in App';
      case 'android':
        return 'Open in App';
      default:
        return 'Open in App Window';
    }
  }
  /**
   * Check if manual installation is possible
   */
  canInstallManually() {
    const platform = this.getPlatform();
    const browser = this.getBrowser();

    // iOS Safari, or desktop browsers that support PWA
    return (
      (platform === 'ios' && browser === 'safari') ||
      platform === 'android' ||
      (['windows', 'mac', 'linux'].includes(platform) && ['chrome', 'edge'].includes(browser))
    );
  }

  /**
   * Get platform-specific install instructions
   */
  getInstallInstructions() {
    const platform = this.getPlatform();

    const instructions = {
      ios: {
        title: 'Install on iPhone/iPad',
        steps: [
          'Tap the Share button (square with arrow up)',
          'Scroll down and tap "Add to Home Screen"',
          'Tap "Add" to install the app',
        ],
      },
      android: {
        title: 'Install on Android',
        steps: [
          'Tap the menu button (three dots)',
          'Select "Add to Home screen" or "Install app"',
          'Tap "Add" to install',
        ],
      },
      desktop: {
        title: `Install on ${platform}`,
        steps: [
          'Look for the install icon (⊞) in the address bar',
          'Or check your browser menu for "Install app"',
          'Click "Install" to add to your applications',
        ],
      },
    };

    if (platform === 'ios') return instructions.ios;
    if (platform === 'android') return instructions.android;
    return instructions.desktop;
  }

  /**
   * Detect platform
   */
  getPlatform() {
    const userAgent = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(userAgent)) return 'ios';
    if (/Android/.test(userAgent)) return 'android';
    if (/Windows/.test(userAgent)) return 'windows';
    if (/Macintosh|Mac OS X/.test(userAgent)) return 'mac';
    if (/Linux/.test(userAgent)) return 'linux';
    return 'unknown';
  }

  /**
   * Detect browser
   */
  getBrowser() {
    const userAgent = navigator.userAgent;
    if (/Edg/.test(userAgent)) return 'edge';
    if (/Chrome/.test(userAgent) && !/Edg/.test(userAgent)) return 'chrome';
    if (/Firefox/.test(userAgent)) return 'firefox';
    if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) return 'safari';
    return 'unknown';
  }

  /**
   * Check if PWA is supported
   */
  supportsPWA() {
    return (
      'serviceWorker' in navigator &&
      (location.protocol === 'https:' || location.hostname === 'localhost')
    );
  }

  /**
   * Subscribe to state changes
   */
  onStateChange(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners
   */
  notifyListeners(event, data) {
    this.listeners.forEach((callback) => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('PWA listener error:', error);
      }
    });
  }

  /**
   * Clear installation state (for testing)
   */
  clearInstallationState() {
    localStorage.removeItem('pwa-installed');
    localStorage.removeItem('pwa-install-date');
    console.log('PWA installation state cleared');
  }
}

// Create singleton instance
export const pwaManager = new SimplePWAManager();

// Expose for debugging
if (typeof window !== 'undefined') {
  window.pwaManager = pwaManager;
}

/**
 * React hook for PWA functionality
 * @param {boolean} isAuthenticated - Whether user is logged in
 */
export const usePWA = (isAuthenticated = false) => {
  const [state, setState] = useState(() => pwaManager.getState(isAuthenticated));

  useEffect(() => {
    const updateState = () => setState(pwaManager.getState(isAuthenticated));

    // Listen for state changes
    const unsubscribe = pwaManager.onStateChange(updateState);

    // Update state periodically to catch external changes
    const interval = setInterval(updateState, 5000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [isAuthenticated]);

  const installPWA = useCallback(async () => {
    return await pwaManager.installPWA();
  }, []);

  const requestNotifications = useCallback(async () => {
    return await pwaManager.requestNotifications();
  }, []);

  const openInApp = useCallback(() => {
    pwaManager.openInApp();
  }, []);

  const clearInstallationState = useCallback(() => {
    pwaManager.clearInstallationState();
  }, []);

  return {
    ...state,
    installPWA,
    requestNotifications,
    openInApp,
    clearInstallationState,
  };
};

export default pwaManager;
